import styled from "styled-components";
import { tokens } from "../../../../../public/tokens";
import hexToRGBA from "../../../../../utils/hexToRGBA";

/*
 * Visual values here are pulled directly from the Career Library careers
 * table's `tableCustomStyles` (front/src/components/careerLibrary/careerLibrary.styles.js)
 * - the AMI system-table reference - not copied from Figma or invented fresh:
 * 34px header row with a border-bottom divider, spacing-4 cell padding,
 * lg-14px body font in text.primary, zebra-striped rows, and the same
 * min-height/padding the Pagination footer already renders at. The structural
 * shape (checkbox column, sortable head buttons, skeleton loading rows) mirrors
 * the sibling `linkedProgrammes/LinkedProgrammesTable.styles.js`, which was
 * confirmed to already conform to these same values.
 */

export const StyledTableScroller = styled.div({
    width: "100%",
    overflowX: "auto",

    border: "0 !important",
    outline: "0 !important",
    boxShadow: "none !important",
});

export const StyledTable = styled.table({
    width: "100%",
    /* Matches the sum of the fixed column widths below exactly, so table-layout: fixed
     * has no leftover width to redistribute unpredictably across columns. */
    minWidth: "1325px",

    tableLayout: "fixed",
    borderCollapse: "collapse",
    borderSpacing: 0,

    border: "0 !important",
    outline: "0 !important",
    boxShadow: "none !important",

    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],

    "tbody tr:nth-child(even)": {
        background: tokens.colors.surfaces["page-background"],
    },

    "thead, tbody, tr": {
        borderLeft: "0 !important",
        borderRight: "0 !important",
        outline: "0 !important",
    },
});

export const StyledHeadCell = styled.th({
    height: "34px",

    padding: `0 ${tokens.globals["spacing-2"]} ${tokens.globals["spacing-1"]}`,

    borderTop: "0 !important",
    borderLeft: "0 !important",
    borderRight: "0 !important",
    borderBottom: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]} !important`,

    outline: "0 !important",

    background: tokens.colors.surfaces["container-background"],

    textAlign: "left",
    verticalAlign: "middle",

    color: tokens.colors.neutral.grey["800"],

    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "20px",

    whiteSpace: "nowrap",

    boxSizing: "border-box",
});

export const StyledCheckboxHeadCell = styled(StyledHeadCell)({
    width: "49px",
    paddingLeft: 0,
    paddingRight: 0,
    textAlign: "center",

    "> *": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});

/*
 * A fixed width (not "auto") - on `table-layout: fixed`, an "auto" column absorbs
 * 100% of any leftover space once the table is wider than its column-width sum,
 * which is what let Portal Name balloon to ~40%+ of the table on typical desktop
 * widths and starve every column after it. 347px matches the Figma reference
 * table's own Portal Name column (346.5px of its 1322px total) - proportionate,
 * not dominant, and every column below is sized the same way off that same
 * reference so table-layout: fixed scales them all together, in proportion, on
 * wider viewports instead of only inflating this one.
 */
export const StyledPortalHeadCell = styled(StyledHeadCell)({
    width: "347px",
});


export const StyledCodeHeadCell = styled(StyledHeadCell)({ width: "74px" });
export const StyledCreditsHeadCell = styled(StyledHeadCell)({ width: "55px" });
export const StyledStudyPathHeadCell = styled(StyledHeadCell)({ width: "114px" });
export const StyledTermHeadCell = styled(StyledHeadCell)({ width: "72px" });
export const StyledYearHeadCell = styled(StyledHeadCell)({ width: "68px" });
export const StyledCountHeadCell = styled(StyledHeadCell)({ width: "55px", textAlign: "center" });
export const StyledNetsuiteHeadCell = styled(StyledHeadCell)({ width: "199px" });
export const StyledPrereqHeadCell = styled(StyledHeadCell)({ width: "188px" });
export const StyledActionsHeadCell = styled(StyledHeadCell)({
    width: "104px",
    paddingLeft: tokens.globals["spacing-2"],
    paddingRight: tokens.globals["spacing-4"],
});

export const StyledCell = styled.td({
    height: "56px",

    padding: `${tokens.globals["spacing-2"]} ${tokens.globals["spacing-2"]}`,

    borderTop: "0 !important",
    borderLeft: "0 !important",
    borderRight: "0 !important",
    borderBottom: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]} !important`,

    outline: "0 !important",

    verticalAlign: "middle",

    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "20px",

    boxSizing: "border-box",
});

export const StyledCheckboxCell = styled(StyledCell)({
    width: "49px",
    paddingLeft: 0,
    paddingRight: 0,
    textAlign: "center",

    "> *": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});

/*
 * `alignItems: flex-start` is the fix here: a flex column's cross-axis (width) default is
 * `stretch`, which was forcing the Badge below the name to a full-row-width bar instead of
 * a compact content-width pill. `justifyContent: center` keeps the two-line group centred
 * in the shared 56px row height.
 */
export const StyledPortalCell = styled(StyledCell)({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: tokens.globals["spacing-1"],
});

export const StyledCountCell = styled(StyledCell)({
    textAlign: "center",

    i: {
        color: tokens.colors.states.warning["500"],
        fontSize: tokens.typography.fontSize["md-12px"],
    },
});


export const StyledCodeCell = styled(StyledCell)({
    whiteSpace: "nowrap",
});

export const StyledCreditsCell = styled(StyledCell)({
    textAlign: "center",
    whiteSpace: "nowrap",
});

export const StyledTermCell = styled(StyledCell)({
    textAlign: "center",
});

export const StyledYearCell = styled(StyledCell)({
    textAlign: "center",
});

export const StyledActionsCell = styled(StyledCell)({
    paddingLeft: tokens.globals["spacing-2"],
    paddingRight: tokens.globals["spacing-4"],
});

export const StyledPortalName = styled.span({
    display: "block",
    color: tokens.colors.text.primary,
    fontWeight: tokens.typography.fontWeights.semibold,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});


export const StyledModuleStatusPill = styled.span(({ $tone = "grey" }) => {
    const tones = {
        success: { bg: hexToRGBA(tokens.colors.states.success["500"], 0.1), fg: tokens.colors.states.success["500"] },
        warning: { bg: tokens.colors.states.warning["100"], fg: tokens.colors.states.warning["800"] },
        grey: { bg: tokens.colors.neutral.grey.Alpha["10"], fg: tokens.colors.text.secondary },
    };
    const tone = tones[$tone] || tones.grey;

    return {
        display: "inline-flex",
        width: "fit-content",
        maxWidth: "100%",
        alignSelf: "flex-start",
        alignItems: "center",
        justifyContent: "center",
        height: "22px",
        padding: `0 ${tokens.globals["spacing-2"]}`,
        borderRadius: tokens.globals["border-radius"],
        background: tone.bg,
        color: tone.fg,
        fontFamily: tokens.typography.fontFamilies["albert-sans"],
        fontSize: tokens.typography.fontSize["md-12px"],
        fontWeight: tokens.typography.fontWeights.semibold,
        whiteSpace: "nowrap",
    };
});

export const StyledStudyPathBadge = styled.span(({ $assigned = false }) => ({
    display: "inline-flex",
    width: "fit-content",
    alignSelf: "flex-start",
    alignItems: "center",
    gap: tokens.globals["spacing-1"],
    height: "18px",
    padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2"]}`,
    borderRadius: tokens.globals["border-radius"],
    border: $assigned ? `1px solid ${tokens.colors.states.info["500"]}` : "none",
    background: $assigned ? tokens.colors.states.info["500"] : tokens.colors.states.alert["100"],
    color: $assigned ? tokens.colors.neutral.white : tokens.colors.ui.destructive,
    fontSize: tokens.typography.fontSize["sm-10px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    whiteSpace: "nowrap",

    i: { fontSize: "8px" },
}));

export const StyledPrereqList = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: tokens.globals["spacing-1"],
    width: "100%",
});

export const StyledPrereqLink = styled.button({
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    padding: 0,
    border: "none",
    background: "transparent",
    color: tokens.colors.text.link,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    textAlign: "left",
    cursor: "pointer",

    "&::before": {
        content: '""',
        flexShrink: 0,
        width: "7px",
        height: "7px",
        borderRadius: "50%",
        background: "currentColor",
    },

    "&:hover": { textDecoration: "underline" },
});

export const StyledSortButton = styled.button({
    padding: 0,
    border: 0,
    background: "transparent",
    color: "inherit",
    font: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
    cursor: "pointer",

    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },

    "&:disabled": { cursor: "not-allowed" },
});

export const StyledSortIcon = styled.i({
    width: "12.5px",
    height: "12.5px",
    flex: "0 0 12.5px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colors.neutral.black,
    fontSize: tokens.typography.fontSize["sm-10px"],
    lineHeight: "10px",
    textAlign: "center",
});

export const StyledMenuWrapper = styled.div({
    position: "relative",
    display: "inline-flex",
});

export const StyledMenu = styled.div({
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    minWidth: "170px",
    padding: `${tokens.globals["spacing-1"]} 0`,
    background: tokens.colors.surfaces["container-background"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.hover,
    zIndex: 20,
});

export const StyledMenuItem = styled.button({
    width: "100%",
    padding: `${tokens.globals["spacing-2"]} ${tokens.globals["spacing-3"]}`,
    border: "none",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],
    color: tokens.colors.ui.destructive,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    textAlign: "left",
    cursor: "pointer",

    "&:hover:not(:disabled)": {
        background: tokens.colors.neutral.grey["100"],
    },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
});

export const StyledLoadingRow = styled.td({
    height: "112px",

    padding: tokens.globals["spacing-6"],

    borderTop: "0 !important",
    borderLeft: "0 !important",
    borderRight: "0 !important",
    borderBottom: `1px solid ${tokens.colors.surfaces["border-divider"]} !important`,

    textAlign: "center",
    color: tokens.colors.text.secondary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
});

export const StyledErrorRow = styled(StyledLoadingRow)({
    color: tokens.colors.states.alert["500"],
});
